import React, { useState, useEffect } from "react";
import { Button, Form, Image, Input, Select, message, Modal } from "antd";

const EditModal = ({ 
  visible, 
  onCancel, 
  onSubmit, 
  record, 
  loading = false 
}) => {
  const [editForm] = Form.useForm();
  const [editImageSrc, setEditImageSrc] = useState([]);
  const [editImageSrcChoice, setEditImageSrcChoice] = useState([]);

  // Sync edit imageSrc with edit form field value for validation
  useEffect(() => {
    editForm.setFieldValue('images', editImageSrc.length > 0 ? 'has-images' : '');
  }, [editImageSrc, editForm]);

  // Sync edit imageSrcChoice with edit form field value for validation  
  useEffect(() => {
    editForm.setFieldValue('imagesChoice', editImageSrcChoice.length > 0 ? 'has-images' : '');
  }, [editImageSrcChoice, editForm]);

  // Set form values when record changes
  useEffect(() => {
    if (record && visible) {
      editForm.setFieldsValue({
        user: record.userId,
        case: record.caseName,
        note: record.note || ''
      });
      
      // Set images from record
      if (record.imgRecipe && record.imgRecipe.length > 0) {
        setEditImageSrc(record.imgRecipe.map(img => img.img_url));
      } else {
        setEditImageSrc([]);
      }
      
      if (record.imgChoice && record.imgChoice.length > 0) {
        setEditImageSrcChoice(record.imgChoice.map(img => img.img_url));
      } else {
        setEditImageSrcChoice([]);
      }
    }
  }, [record, visible, editForm]);

  const handleEditPaste = (event) => {
    const items = event.clipboardData.items;
    for (const item of items) {
      if (item.type.indexOf("image") === 0) {
        const file = item.getAsFile();
        const reader = new FileReader();
        reader.onload = (e) => {
          setEditImageSrc([...editImageSrc, e.target.result]);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleEditChoicePaste = (event) => {
    const items = event.clipboardData.items;
    for (const item of items) {
      if (item.type.indexOf("image") === 0) {
        const file = item.getAsFile();
        const reader = new FileReader();
        reader.onload = (e) => {
          setEditImageSrcChoice([...editImageSrcChoice, e.target.result]);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await editForm.validateFields();
      
      const formattedImages = editImageSrc.map((image) =>
        image.replace(/^data:image\/(png|jpeg|jpg);base64,/, "")
      );

      const formattedImagesChoice = editImageSrcChoice.map((image) =>
        image.replace(/^data:image\/(png|jpeg|jpg);base64,/, "")
      );

      const formattedValues = {
        id: record?.id,
        caseName: values.case,
        userId: values.user,
        note: values.note,
        img: formattedImages,
        imgChoice: formattedImagesChoice,
      };

      await onSubmit(formattedValues);
      
      // Reset form and images
      setEditImageSrc([]);
      setEditImageSrcChoice([]);
      editForm.resetFields();
    } catch (error) {
      message.error('ไม่สามารถอัพเดตข้อมูลได้');
    }
  };

  const handleCancel = () => {
    setEditImageSrc([]);
    setEditImageSrcChoice([]);
    editForm.resetFields();
    onCancel();
  };

  const isInform = record?.status === "Inform";
  const informImages = record?.imgInform || [];

  return (
    <Modal
      title={isInform ? "เติมข้อมูล" : "แก้ไขข้อมูล"}
      open={visible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      width={800}
      okText="บันทึก"
      cancelText="ยกเลิก"
      confirmLoading={loading}
    >
      {/* หลักฐานที่ฝั่ง panel แนบมาตอนขอข้อมูลเพิ่ม — อ่านอย่างเดียว
          ห้ามเอาเข้า editImageSrc เด็ดขาด ไม่งั้นจะถูกส่งกลับไปบันทึกซ้ำเป็นรูปสลิป */}
      {isInform && (record?.inform_remark || informImages.length > 0) && (
        <div
          style={{
            border: "1px solid #ffd591",
            background: "#fff7e6",
            borderRadius: 6,
            padding: 12,
            marginBottom: 16,
          }}
        >
          <div style={{ fontWeight: 600, color: "#d46b08", marginBottom: 6 }}>
            ข้อมูลที่ขอเพิ่ม
          </div>
          {record?.inform_remark && (
            <div style={{ whiteSpace: "pre-wrap", marginBottom: informImages.length ? 10 : 0 }}>
              {record.inform_remark}
            </div>
          )}
          {informImages.length > 0 && (
            <Image.PreviewGroup>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {informImages.map((img, i) => (
                  <Image
                    key={img.id ?? i}
                    src={img.img_url}
                    alt={`หลักฐาน ${i + 1}`}
                    height={110}
                    style={{ objectFit: "cover", borderRadius: 4 }}
                  />
                ))}
              </div>
            </Image.PreviewGroup>
          )}
        </div>
      )}
      <Form form={editForm} layout="vertical">
        <Form.Item
          name="user"
          label="User"
          rules={[{ required: true, message: "Required" }]}
        >
          <Input maxLength={100} placeholder="UserId" />
        </Form.Item>
        <Form.Item
          name="case"
          label="Case"
          rules={[{ required: true, message: "Please select case!" }]}
        >
          <Select>
            <Select.Option value="deposit">Deposit</Select.Option>
            <Select.Option value="withdraw">Withdraw</Select.Option>
            <Select.Option value="ETC">ETC.</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item 
          name="images" 
          label="รูปภาพ(receipt)"
          dependencies={['case']}
          rules={[
            { 
              required: false, 
              validator: (_, value) => {
                const caseValue = editForm.getFieldValue('case');
                if (caseValue === 'deposit' && editImageSrc.length === 0) {
                  return Promise.reject(new Error('กรุณาอัพโหลดรูปภาพ Receipt สำหรับ Deposit'));
                }
                return Promise.resolve();
              }
            }
          ]}
        >
          <div>
            <textarea
              placeholder="กด Ctrl+V เพื่อวางรูป"
              onPaste={handleEditPaste}
              rows={2}
              style={{
                width: "100%",
                padding: "8px",
                border: "1px solid #d9d9d9",
                borderRadius: "6px",
              }}
            />
            {editImageSrc.length > 0 && (
              <>
                <div className="mt-2 mb-1">
                  <strong>Preview รูป:</strong>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "10px",
                  }}
                >
                  {editImageSrc.map((src, index) => (
                    <div
                      key={index}
                      style={{ width: "100px", position: "relative" }}
                    >
                      <Button
                        type="text"
                        danger
                        style={{
                          position: "absolute",
                          top: -10,
                          right: -10,
                          padding: 0,
                          minWidth: "20px",
                          height: "20px",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "white",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                        }}
                        onClick={() => {
                          const newImageSrc = [...editImageSrc];
                          newImageSrc.splice(index, 1);
                          setEditImageSrc(newImageSrc);
                        }}
                      >
                        ×
                      </Button>
                      <img
                        src={src}
                        alt="pasted"
                        style={{
                          maxWidth: "100%",
                          height: "auto",
                          borderRadius: "4px",
                        }}
                      />
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </Form.Item>

        <Form.Item
          name="imagesChoice"
          label="รูปภาพ(เสริม)"
          required={false}
        >
          <div>
            <textarea
              placeholder="กด Ctrl+V เพื่อวางรูป"
              onPaste={handleEditChoicePaste}
              rows={2}
              style={{
                width: "100%",
                padding: "8px",
                border: "1px solid #d9d9d9",
                borderRadius: "6px",
              }}
            />
            {editImageSrcChoice.length > 0 && (
              <>
                <div className="mt-2 mb-1">
                  <strong>Preview รูป:</strong>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "10px",
                  }}
                >
                  {editImageSrcChoice.map((src, index) => (
                    <div
                      key={index}
                      style={{ width: "100px", position: "relative" }}
                    >
                      <Button
                        type="text"
                        danger
                        style={{
                          position: "absolute",
                          top: -10,
                          right: -10,
                          padding: 0,
                          minWidth: "20px",
                          height: "20px",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "white",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                        }}
                        onClick={() => {
                          const newImageSrc = [...editImageSrcChoice];
                          newImageSrc.splice(index, 1);
                          setEditImageSrcChoice(newImageSrc);
                        }}
                      >
                        ×
                      </Button>
                      <img
                        src={src}
                        alt="pasted"
                        style={{
                          maxWidth: "100%",
                          height: "auto",
                          borderRadius: "4px",
                        }}
                      />
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </Form.Item>

        <Form.Item name="note" label="Note">
          <Input.TextArea rows={4} placeholder="Enter Note" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditModal; 